// AI Studio session tabs (POR-14795) — pure helpers shared by the shell
// (AiStudioSessions.vue), the postMessage bridge and the store. No DOM, no Vue,
// relative imports only: tests/js/ai-studio-session-tabs.test.mjs runs them in Node.

export const MAIN_SESSION_KEY = 'main';
export const MAX_SESSIONS = 5;
export const BRIDGE_NAMESPACE = 'ai-studio-sessions';

const SESSION_KEY_PATTERN = /^[a-z0-9]{8,32}$/;
const SESSION_KEY_LENGTH = 12;
const DESIGN_JOBS_KEY_PREFIX = 'ai-chat:creative-studio';

export function newSessionKey(random = Math.random) {
  let key = '';
  while (key.length < SESSION_KEY_LENGTH) {
    key += random().toString(36).slice(2);
  }
  return key.slice(0, SESSION_KEY_LENGTH);
}

export function isValidSessionKey(key) {
  return typeof key === 'string' && key !== MAIN_SESSION_KEY && SESSION_KEY_PATTERN.test(key);
}

// A conversation id as the tab list stores it: a positive integer, or null for a blank chat.
export function normalizeConversationId(value) {
  if (value === null || value === undefined || value === '') return null;
  const id = Number(value);
  return Number.isInteger(id) && id > 0 ? id : null;
}

// `tabs` are the EXTRA sessions; the main session always exists.
export function canOpenSession(tabs) {
  return 1 + (Array.isArray(tabs) ? tabs.length : 0) < MAX_SESSIONS;
}

export function addSessionTab(tabs, key, extra = {}) {
  if (!canOpenSession(tabs)) return tabs;
  return [...tabs, { ...extra, key, conversationId: null }];
}

export function removeSessionTab(tabs, key) {
  return tabs.filter((tab) => tab.key !== key);
}

export function setTabConversation(tabs, key, conversationId) {
  const id = normalizeConversationId(conversationId);
  return tabs.map((tab) => (tab.key === key ? { ...tab, conversationId: id } : tab));
}

// The tab to show after `closedKey` closes: the one before it, else main.
export function neighborAfterClose(tabs, closedKey) {
  const index = tabs.findIndex((tab) => tab.key === closedKey);
  return index > 0 ? tabs[index - 1].key : MAIN_SESSION_KEY;
}

// The session to activate after `closedKey` closes, or null when the active
// session stays as it is (closing a background tab must not switch or scroll).
export function nextActiveAfterClose(tabs, closedKey, activeKey) {
  return closedKey === activeKey ? neighborAfterClose(tabs, closedKey) : null;
}

// A page is an extra session tab only when it is framed by the shell. The same
// URL opened on its own (e.g. "Open frame in new tab") behaves as a normal page.
export function resolveSessionTabKey(metaValue, isFramed) {
  return isFramed && metaValue ? metaValue : null;
}

export function sessionTabsStorageKey(companyId) {
  return `creative-studio:session-tabs:${companyId || 'unknown'}`;
}

export function serializeSessionTabs(tabs, activeKey) {
  return JSON.stringify({
    tabs: tabs.map((tab) => ({ key: tab.key, conversationId: normalizeConversationId(tab.conversationId) })),
    activeKey,
  });
}

// Rebuild the tab list from storage, dropping anything another build, another
// window or a hand edit could have left there.
export function restoreSessionTabs(raw) {
  let parsed = raw;
  if (typeof raw === 'string') {
    try {
      parsed = JSON.parse(raw);
    } catch (e) {
      parsed = null;
    }
  }

  const tabs = [];
  const keys = new Set();
  const conversations = new Set();
  const stored = parsed && Array.isArray(parsed.tabs) ? parsed.tabs : [];

  stored.forEach((tab) => {
    if (!tab || !isValidSessionKey(tab.key) || keys.has(tab.key)) return;
    if (!canOpenSession(tabs)) return;
    const conversationId = normalizeConversationId(tab.conversationId);
    if (conversationId && conversations.has(conversationId)) return;
    keys.add(tab.key);
    if (conversationId) conversations.add(conversationId);
    tabs.push({ key: tab.key, conversationId });
  });

  const activeKey = parsed && keys.has(parsed.activeKey) ? parsed.activeKey : MAIN_SESSION_KEY;
  return { tabs, activeKey };
}

export function sessionTabUrl(pathname, key, conversationId) {
  const conversation = normalizeConversationId(conversationId) || 'new';
  return `${pathname}?session_tab=${encodeURIComponent(key)}&conversation=${conversation}`;
}

// Conversations open in sessions other than `key`: that session must not open them itself.
export function conversationsOpenElsewhere(sessions, key) {
  return sessions
    .filter((session) => session.key !== key)
    .map((session) => normalizeConversationId(session.conversationId))
    .filter(Boolean);
}

export function sessionHoldingConversation(sessions, conversationId, exceptKey) {
  const id = normalizeConversationId(conversationId);
  if (!id) return null;
  const holder = sessions.find((session) => session.key !== exceptKey
    && normalizeConversationId(session.conversationId) === id);
  return holder ? holder.key : null;
}

// Leaving AI Studio from `fromKey` would stop work running in another session.
export function othersBusy(sessions, fromKey) {
  return sessions.some((session) => session.key !== fromKey && session.busy);
}

// The "finished" dot: set when a session stops being busy while the user looks
// at another tab; cleared as soon as that tab is the active one.
export function nextUnseenDone({ wasBusy, isBusy, isActive, unseen }) {
  if (isActive) return false;
  if (wasBusy && !isBusy) return true;
  return !!unseen;
}

// The main session keeps today's keys; an extra tab gets its own pair, so its
// "New" never wipes another session's recovery list and the main session's
// mount-time sweep (handleCsUuidQueryParam) never sees a tab's jobs.
export function designJobsStorageKeys(tabKey) {
  const suffix = tabKey ? `:${tabKey}` : '';
  return {
    pending: `${DESIGN_JOBS_KEY_PREFIX}:pending-design-jobs${suffix}`,
    resolved: `${DESIGN_JOBS_KEY_PREFIX}:resolved-design-jobs${suffix}`,
  };
}

export function isBridgeMessage(data, dir) {
  return !!data
    && typeof data === 'object'
    && data.ns === BRIDGE_NAMESPACE
    && data.dir === dir
    && typeof data.type === 'string';
}

// A session tab always asks the shell (it must never navigate inside its iframe,
// even before the shell's first message); the main session asks only when
// other sessions exist.
export function leaveThroughShell(inSessionTab, shellInfo) {
  if (inSessionTab) return true;
  return !!shellInfo && shellInfo.count > 1;
}

export function sessionTitle(title) {
  const text = typeof title === 'string' ? title.trim() : '';
  return text || 'New chat';
}
